import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { placesLeft, placesTaken, saleEndStillAhead } from "@/domain/today/rules";
import type { OccupiedPlaces, TodayOffer } from "@/domain/today/types";
import { czechFrom } from "@/lib/czech";
import { formatTime } from "@/lib/time";
import { TodaySection } from "./TodaySection";

interface TodaysOffersProps {
  offers: TodayOffer[];
}

function occupiedParts(places: OccupiedPlaces) {
  if (places.kind === "split") {
    return [
      { label: "Zaplaceno", count: places.paid, swatch: "bg-success" },
      { label: "Čeká na potvrzení", count: places.awaitingConfirmation, swatch: "bg-primary" },
    ];
  }
  return [{ label: "Obsazeno", count: places.occupied, swatch: "bg-muted-foreground" }];
}

/** An oversold slot would push the bar past its track, so the parts are shown
 *  against whichever is larger — the capacity or what is actually held. */
function widthFor(count: number, places: number): string {
  if (places <= 0) return "0%";
  return `${(count / places) * 100}%`;
}

function OccupancyBar({ offer }: { offer: TodayOffer }) {
  const places = Math.max(offer.capacity, placesTaken(offer));

  return (
    <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
      {occupiedParts(offer.occupiedPlaces)
        .filter((part) => part.count > 0)
        .map((part) => (
          <div
            key={part.label}
            className={part.swatch}
            style={{ width: widthFor(part.count, places) }}
          />
        ))}
    </div>
  );
}

/** The legend is the bar's text equivalent, not decoration beside it: the counts
 *  are the whole of what the bar says, so a partner who cannot see the colours
 *  reads the same sentence. It sits outside the link so the row's jump into the
 *  sleva is not named after every number on it. */
function OccupancyLegend({ offer }: { offer: TodayOffer }) {
  const parts = [
    ...occupiedParts(offer.occupiedPlaces),
    { label: "Volno", count: placesLeft(offer), swatch: "bg-muted" },
  ];

  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
      {parts.map((part) => (
        <span key={part.label} className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${part.swatch}`} aria-hidden="true" />
          {part.label} <span className="tabular-nums">{part.count}</span>
        </span>
      ))}
      <span className="tabular-nums">
        {czechFrom(offer.capacity)} {offer.capacity} míst
      </span>
    </p>
  );
}

export function TodaysOffers({ offers }: TodaysOffersProps) {
  return (
    <TodaySection
      icon={<CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />}
      title="Dnešní termíny"
      count={offers.length}
      explanation="Všechny termíny, které dnes začínají — vyprodané i volné. Pruh ukazuje, kolik míst je zaplacených, kolik čeká na vaše potvrzení a kolik zbývá volných. Kliknutím se dostanete na slevový termín."
    >
      {offers.length === 0 ? (
        <p className="text-sm text-muted-foreground">Dnes nezačíná žádný termín.</p>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {offers.map((offer) => {
            const sellingUntil = saleEndStillAhead(offer);
            return (
              <li key={offer.offerId} className="flex flex-col gap-2 py-4">
                <Link
                  to={`/offers?offer=${offer.offerId}`}
                  className="font-medium hover:text-primary transition-colors"
                >
                  <span className="tabular-nums">{formatTime(offer.startTime)}</span> ·{" "}
                  {offer.activityName}
                  {sellingUntil ? (
                    <span className="text-sm font-normal text-muted-foreground">
                      {" "}
                      · prodej do {formatTime(sellingUntil)}
                    </span>
                  ) : null}
                </Link>
                <OccupancyBar offer={offer} />
                <OccupancyLegend offer={offer} />
              </li>
            );
          })}
        </ul>
      )}
    </TodaySection>
  );
}
